import React from 'react'
import { batch } from 'react-redux'
import history from 'browserHistory'
import RoutesHelper from 'helpers/RoutesHelper'
import { apiCapableActionCreator } from '../apiActionHelpers'
import NotesAPI from 'remote/notes'
import * as UISelectors from 'data/ui/selectors'
import * as UIActions from 'data/ui/actions'
import * as ChannelsSelectors from 'data/channels/selectors'
import * as NotesSelectors from 'data/notes/selectors'
import { dataFetched, dataRemoved } from 'data/graphql'
import { trackMoveTo, trackNewNoteClicked } from 'data/analytics/actions'
import ToasterRedirectLinkAction from 'components/Utils/Toaster/actions/redirectLink'
import * as FilterTypes from 'data/filters/types'

import { trackDisableDirectNoteEdit } from 'data/analytics/actions'

import {
  UPDATE_NOTE_ATTRIBUTES,
  DELETE_NOTES,
  DELETED_NOTES,
  CREATE_NOTE,
  MOVED_NOTES_TO,
  MOVE_NOTES_TO,
  DUPLICATE_NOTE,
  ARCHIVE_NOTES,
  ARCHIVED_NOTES,
  UNARCHIVE_NOTES,
  UNARCHIVED_NOTES,
  BOOKMARKED_NOTE_IDS_FETCHED,
  SHARE_NOTE_TO_EMAIL,
} from './actionTypes'
import { formatDateToDefaultPosition } from '../../screens/Sidebar/NewSidebar/helpers/formatDateToDefaultPosition'

function createNoteRequest(
  { dispatch, apiConfig },
  { channelId, parentNoteId, id, title, initialDelta, redirect = true }
) {
  function onSuccess(note) {
    dispatch(noteCreated(note))
    if (redirect) {
      history.push(
        RoutesHelper.noteChannelShow({
          channelId,
          noteId: note.id,
        })
      )
    }
  }

  function onError(errors) {
    const title = errors[0].message
    dispatch(UIActions.createErrorToasterMessage({ title }))
  }

  return NotesAPI.createNote(apiConfig, {
    id,
    title: title || '',
    channelId,
    parentNoteId,
    snapshot: initialDelta || [{ insert: '\n' }],
  })
    .then(onSuccess)
    .catch(onError)
}

// Helper used by createNote
const createNoteInCurrentContext = (
  { getState, dispatch, apiConfig },
  { title, initialDelta } = {}
) => {
  const state = getState()
  const channelId =
    UISelectors.getActiveChannelId(state) ||
    ChannelsSelectors.getPersonalChannel(state).get('id')

  dispatch(trackNewNoteClicked({ source: 'new_note_button', depth: 0 }))

  return createNoteRequest(
    { dispatch, apiConfig },
    { channelId, title, initialDelta }
  )
}

export const createNote = apiCapableActionCreator(
  ({ channelId, parentNoteId, id, redirect = true } = {}) => ({
    apiConfig,
    dispatch,
    getState,
  }) => {
    return {
      type: CREATE_NOTE,
      payload: channelId
        ? createNoteRequest(
            {
              getState,
              dispatch,
              apiConfig,
            },
            { channelId, parentNoteId, id, redirect }
          )
        : createNoteInCurrentContext({
            getState,
            dispatch,
            apiConfig,
          }),
    }
  }
)

export const shareNoteToEmail = apiCapableActionCreator(
  ({ input }) => ({ dispatch, apiConfig }) => {
    const promise = NotesAPI.shareNoteToEmail(apiConfig, {
      input,
    })

    promise.catch(() => {
      dispatch(
        UIActions.createErrorToasterMessage({
          title: 'Error while sharing doc',
        })
      )
    })

    promise.then(() => {
      dispatch(
        UIActions.createSuccessToasterMessage({
          title: 'Doc successfully shared',
        })
      )
    })

    return {
      type: SHARE_NOTE_TO_EMAIL,
      payload: {
        promise,
      },
    }
  }
)

export const duplicateNote = ({ noteId }) => ({
  dispatch,
  getState,
  graphQLClient,
}) => {
  const state = getState()

  const note = NotesSelectors.getNote(state, noteId)
  const parentNoteId = note && note.parentNote && note.parentNote.id
  const channelId = note.channelId

  return {
    type: DUPLICATE_NOTE,
    payload: {
      promise: graphQLClient
        .mutate({
          query: NotesAPI.duplicateNote,
          variables: {
            input: { noteId, channelId, parentNoteId },
          },
        })
        .then(function onSuccess({ data }) {
          batch(() => {
            const createdNote = data.duplicateNote
            dispatch(noteCreated(createdNote))
            const redirectTo = RoutesHelper.noteChannelShow({
              channelId,
              noteId: createdNote.id,
            })
            history.push(redirectTo)
          })
        }),
    },
  }
}

export function notesMoved({
  noteIds,
  channelId,
  parentNoteId = null,
  listPosition,
}) {
  const nodes = noteIds.map(noteId => ({
    id: noteId,
    __typename: 'Note',
    channelId,
    parentNote: parentNoteId
      ? {
          id: parentNoteId,
          __typename: 'Note',
        }
      : null,
    ...(listPosition != null ? { listPosition } : {}), // avoid specifying listPosition if not provided
  }))

  if (parentNoteId) {
    nodes.push({ id: parentNoteId, __typename: 'Note', hasChildren: true })
  }

  return {
    type: MOVED_NOTES_TO,
    payload: nodes,
    metadata: {
      graphql: dataFetched(nodes),
    },
  }
}

export const moveNotesTo = apiCapableActionCreator(
  ({
    noteIds,
    channelId,
    analyticsSource,
    parentNoteId,
    position,
    toaster = {
      id: 'move-notes-success',
      title:
        noteIds.length === 1
          ? `One doc successfully moved.`
          : `${noteIds.length} docs successfully moved.`,
      renderAction: toasterMessage => {
        let link = RoutesHelper.channelShow({
          channelId,
        })

        if (noteIds.length === 1) {
          // moving a single, go to that note
          link = RoutesHelper.noteShow({
            noteId: noteIds[0],
          })
        } else {
          // when moving many notes, go to the target parent if not a channel
          if (parentNoteId) {
            link = RoutesHelper.noteShow({
              noteId: parentNoteId,
            })
          }
        }
        return (
          <ToasterRedirectLinkAction
            isInternal={true}
            linkTarget={link}
            toasterMessage={toasterMessage}
          >
            Go to the destination
          </ToasterRedirectLinkAction>
        )
      },
    },
  }) => ({ dispatch, apiConfig }) => {
    const promise = NotesAPI.moveNotesTo(apiConfig, {
      ids: noteIds,
      channelId,
      parentNoteId,
      position,
    })
    promise.catch(errors => {
      dispatch(
        UIActions.createErrorToasterMessage({
          title: 'Unable to move these docs',
          detail: errors[0].errorType === 'UserError' && errors[0].message,
        })
      )
    })

    promise.then(() => {
      batch(() => {
        dispatch(trackMoveTo({ source: analyticsSource }))
        dispatch(
          notesMoved({
            noteIds,
            channelId,
            parentNoteId,
            listPosition: position,
          })
        )
        dispatch(dispatch(UIActions.resetSelectedNoteIds()))
        if (toaster) {
          dispatch(UIActions.createSuccessToasterMessage(toaster))
        }
      })
    })

    return {
      type: MOVE_NOTES_TO,
      payload: {
        promise,
      },
    }
  }
)

export const moveNoteToSibling = (noteId, siblingId, channelId, position) => ({
  dispatch,
  getState,
}) => {
  const state = getState()
  const siblingNote = NotesSelectors.getNote(state, siblingId)
  const parentNoteId =
    siblingNote.parentNote && siblingNote.parentNote.get('id')
  const siblings = NotesSelectors.getAllNotes(state)
    .filter(note => {
      if (!parentNoteId) {
        return note.channelId === channelId
      }
      return (
        (note.parentNote ? note.parentNote.get('id') : note.parentNoteId) ===
        parentNoteId
      )
    })
    .sort((a, b) => b.listPosition - a.listPosition)
    .toArray()
  const siblingIndex = siblings.findIndex(note => note.id === siblingId)

  let computedPosition
  if (position === 'top') {
    const previousSibling = siblings[siblingIndex - 1]
    computedPosition = previousSibling
      ? (previousSibling.listPosition + siblingNote.listPosition) / 2
      : formatDateToDefaultPosition()
  } else {
    const nextSibling = siblings[siblingIndex + 1]
    computedPosition = nextSibling
      ? (nextSibling.listPosition + siblingNote.listPosition) / 2
      : siblingNote.listPosition / 2
  }

  dispatch(
    moveNotesTo({
      noteIds: [noteId],
      parentNoteId,
      channelId,
      position: computedPosition,
      analyticsSource: 'dnd',
      toaster: false,
    })
  )
}

export const updateNoteAttributes = apiCapableActionCreator(
  ({ noteId, title }) => ({ dispatch, apiConfig }) => ({
    type: UPDATE_NOTE_ATTRIBUTES,
    payload: {
      id: noteId,
      title,
      promise: NotesAPI.updateNote(apiConfig, {
        id: noteId,
        title,
      }).then(data => {
        dispatch(dataFetched(data))
        return data
      }),
    },
  })
)

export function notesDeleted({ noteIds }) {
  const nodes = noteIds.map(noteId => ({
    id: noteId,
    __typename: 'Note',
  }))
  return {
    type: DELETED_NOTES,
    payload: nodes,
    metadata: {
      noteIdsToRedirect: noteIds,
      graphql: dataRemoved(nodes),
    },
  }
}

export const deleteNotes = apiCapableActionCreator(
  ({ noteIds }) => ({ apiConfig, dispatch }) => {
    const promise = NotesAPI.deleteNotes(apiConfig, { ids: noteIds })
    promise.catch(errors => {
      dispatch(
        UIActions.createErrorToasterMessage({
          title: 'Unable to delete these docs',
          detail: errors[0].errorType === 'UserError' && errors[0].message,
        })
      )
    })

    promise.then(() => {
      batch(() => {
        dispatch(notesDeleted({ noteIds }))
        dispatch(UIActions.resetSelectedNoteIds())
        dispatch(
          UIActions.createSuccessToasterMessage({
            id: 'delete-notes-success',
            title:
              noteIds.length === 1
                ? `One doc successfully deleted.`
                : `${noteIds.length} docs successfully deleted.`,
          })
        )
      })
    })

    return {
      type: DELETE_NOTES,
      payload: {
        promise,
      },
    }
  }
)

export function notesArchived({ noteIds }) {
  return ({ getState, dispatch }) => {
    const state = getState()
    const nodes = noteIds
      .map(noteId => {
        // only push udpates to notes we have in the store to avoid putting partial stuff in it
        const note = NotesSelectors.getNote(state, noteId)
        if (note) {
          return {
            id: noteId,
            __typename: 'Note',
            archivedAt: new Date(),
          }
        }
        return null
      })
      .filter(Boolean)
    dispatch({
      type: ARCHIVED_NOTES,
      payload: nodes,
      metadata: {
        noteIdsToRedirect: noteIds,
        graphql: dataFetched(nodes),
      },
    })
  }
}

export const archiveNotes = apiCapableActionCreator(
  ({ noteIds }) => ({ getState, dispatch, apiConfig }) => {
    const promise = NotesAPI.archiveNotes(apiConfig, { ids: noteIds })

    promise.catch(errors => {
      dispatch(
        UIActions.createErrorToasterMessage({
          title: 'Unable to archive these docs',
          detail: errors[0].errorType === 'UserError' && errors[0].message,
        })
      )
    })

    promise.then(() => {
      batch(() => {
        dispatch(notesArchived({ noteIds }))
        dispatch(UIActions.resetSelectedNoteIds())
        dispatch(
          UIActions.createSuccessToasterMessage({
            id: 'archives-notes-success',
            title:
              noteIds.length === 1
                ? `Successfully sent to trash.`
                : `${noteIds.length} docs sent to trash.`,
            renderAction: toasterMessage => {
              return (
                <ToasterRedirectLinkAction
                  isInternal={true}
                  linkTarget={RoutesHelper.filterShow({
                    filterId: FilterTypes.ARCHIVED_NOTES.id,
                  })}
                  toasterMessage={toasterMessage}
                >
                  Go to Trash
                </ToasterRedirectLinkAction>
              )
            },
          })
        )

        // if user was on one of the archived note, navigate back to the current
        // top level view: channel or filter
        const activeNoteId = UISelectors.getActiveNoteId(getState())
        if (noteIds.includes(activeNoteId)) {
          const activeChannelId = UISelectors.getActiveChannelId(getState())
          if (activeChannelId) {
            history.push(
              RoutesHelper.channelShow({ channelId: activeChannelId })
            )
            return
          }
          const activeFilterId = UISelectors.getActiveFilterId(getState())
          if (activeFilterId) {
            history.push(RoutesHelper.filterShow({ filterId: activeFilterId }))
          }
        }
      })
    })

    return {
      type: ARCHIVE_NOTES,
      payload: {
        promise,
      },
    }
  }
)

export function notesUnarchived({ noteIds }) {
  return ({ getState, dispatch }) => {
    const state = getState()
    const nodes = noteIds
      .map(noteId => {
        // only push udpates to notes we have in the store to avoid putting partial stuff in it
        const note = NotesSelectors.getNote(state, noteId)
        if (note) {
          return {
            id: noteId,
            __typename: 'Note',
            archivedAt: null,
          }
        }
        return null
      })
      .filter(Boolean)

    dispatch({
      type: UNARCHIVED_NOTES,
      payload: nodes,
      metadata: {
        noteIdsToRedirect: noteIds,
        graphql: dataFetched(nodes),
      },
    })
  }
}

export const unarchiveNotes = apiCapableActionCreator(
  ({ noteIds }) => ({ getState, apiConfig, dispatch }) => {
    const promise = NotesAPI.unarchiveNotes(apiConfig, { ids: noteIds })

    promise.catch(errors => {
      dispatch(
        UIActions.createErrorToasterMessage({
          title: 'Unable to archive these docs',
          detail: errors[0].errorType === 'UserError' && errors[0].message,
        })
      )
    })

    promise.then(() => {
      batch(() => {
        dispatch(notesUnarchived({ noteIds }))
        dispatch(UIActions.resetSelectedNoteIds())
        dispatch(
          UIActions.createSuccessToasterMessage({
            id: 'unarchive-notes-success',
            title:
              noteIds.length === 1
                ? `One doc successfully restored.`
                : `${noteIds.length} docs successfully restored.`,
          })
        )

        // if user was on one of the archived note, navigate back to the current
        // top level view: channel or filter
        const activeNoteId = UISelectors.getActiveNoteId(getState())
        if (noteIds.includes(activeNoteId)) {
          const activeChannelId = UISelectors.getActiveChannelId(getState())
          if (activeChannelId) {
            history.push(
              RoutesHelper.channelShow({ channelId: activeChannelId })
            )
            return
          }
          const activeFilterId = UISelectors.getActiveFilterId(getState())
          if (activeFilterId) {
            history.push(RoutesHelper.filterShow({ filterId: activeFilterId }))
          }
        }
      })
    })

    return {
      type: UNARCHIVE_NOTES,
      payload: {
        promise,
      },
    }
  }
)

export function acknowledgeFileImportSuccess({ channelName, channelId }) {
  return UIActions.createSuccessToasterMessage({
    title: `Your data finished importing.`,
    detail: `Your file(s) have been imported into the '${channelName}' channel.`,
    renderAction: toasterMessage => {
      return (
        <ToasterRedirectLinkAction
          isInternal={true}
          linkTarget={RoutesHelper.channelShow({ channelId })}
          toasterMessage={toasterMessage}
        >
          Go to the channel
        </ToasterRedirectLinkAction>
      )
    },
  })
}

export function acknowledgeFileImportFailed(fileName, channelName) {
  return UIActions.createErrorToasterMessage({
    title: `An error occured while importing a file`,
    detail: `The file ${fileName} couldn't be imported in the channel '${channelName}'.
    Please try again or reach out to the support if the problem persists.`,
  })
}

export function noteCreated(note, meta) {
  return {
    type: 'noteCreated',
    payload: note,
    metadata: {
      graphql: dataFetched(note, meta),
    },
  }
}

export function bookmarkedNotesFetched(notes, meta) {
  const noteIds = notes.reduce((ids, note) => ids.concat(note.id), [])
  return {
    type: BOOKMARKED_NOTE_IDS_FETCHED,
    payload: noteIds,
    metadata: {
      graphql: dataFetched(notes, meta),
    },
  }
}

export function bookmarkedNoteIdsFetched(noteIds) {
  return {
    type: BOOKMARKED_NOTE_IDS_FETCHED,
    payload: noteIds,
  }
}

export function lockNoteForEdition({ id }) {
  return function({ dispatch, graphQLClient }) {
    return {
      type: 'IGNORE_ME',
      payload: {
        promise: graphQLClient
          .mutate({
            query: NotesAPI.lockNoteForEdition,
            variables: { id },
          })
          .then(({ data }) => {
            batch(() => {
              dispatch(trackDisableDirectNoteEdit())
              dispatch(dataFetched(data.lockNoteForEdition))
            })
          })
          .catch(errors => {
            dispatch(
              UIActions.createErrorToasterMessage({
                title: `Unable to change the doc's edition lock status. Please try again or reach out to support if the problem persists.`,
                detail:
                  errors[0].errorType === 'UserError' && errors[0].message,
              })
            )
          }),
      },
    }
  }
}

export function unlockNoteForEdition({ id }) {
  return function({ dispatch, graphQLClient }) {
    return {
      type: 'IGNORE_ME',
      payload: {
        promise: graphQLClient
          .mutate({
            query: NotesAPI.unlockNoteForEdition,
            variables: { id },
          })
          .then(({ data }) => {
            dispatch(dataFetched(data.unlockNoteForEdition))
          })
          .catch(errors => {
            dispatch(
              UIActions.createErrorToasterMessage({
                title: `Unable to change the doc's edition lock status. Please try again or reach out to support if the problem persists.`,
                detail:
                  errors[0].errorType === 'UserError' && errors[0].message,
              })
            )
          }),
      },
    }
  }
}
